import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.status)) {
          setState("failed");
          return;
        }
      } catch (e) {
        console.error("Kunde inte läsa betalstatus", e);
      }
      if (tries >= 12) {
        setState("pending");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4" data-testid="payment-success-page">
      <div className="w-full max-w-sm rounded-lg border border-slate-200 bg-white p-6 text-center">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600" />
            <p className="mt-3 text-sm text-slate-600" data-testid="payment-checking">{t("pay.verifying")}</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-10 w-10 text-green-600" />
            <h1 className="mt-3 text-xl font-semibold text-slate-900" data-testid="payment-paid">{t("pay.paid")}</h1>
            <p className="mt-1 text-sm text-slate-600">{t("pay.paidHint")}</p>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-amber-600" />
            <h1 className="mt-3 text-xl font-semibold text-slate-900" data-testid="payment-pending">{t("pay.pending")}</h1>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="mx-auto h-10 w-10 text-red-600" />
            <h1 className="mt-3 text-xl font-semibold text-slate-900" data-testid="payment-failed">{t("pay.failed")}</h1>
          </>
        )}
        <Button
          data-testid="payment-continue"
          onClick={() => navigate("/abonnemang")}
          className="mt-4 h-11 w-full bg-blue-600 text-white transition-colors duration-200 hover:bg-blue-700"
        >
          {t("pay.continue")}
        </Button>
      </div>
    </div>
  );
}
